import React, { useEffect, useState } from "react";

import SideMenu from "../../../components/SideMenu";
import Footer from "../../../components/Footer";
import Quotes from "../../../components/Quotes";
import { getTranslations } from "../../../services/languageUtils";
import CashOutService from "../../../services/Gowd/cashOutService";
import { BANKS } from "../../../utils/banks";

/**
 * TED para conta bancária.
 *
 * ── O mesmo endpoint do PIX ──
 *
 * O TED sai pela MESMA ordem de payout: o que muda é o método e o destino, que passa a ser uma
 * conta bancária em vez de uma chave. Chave PIX, QR Code e conta bancária são mutuamente
 * exclusivos — o servidor recusa dois juntos, porque descreveria dois destinos para o mesmo
 * pagamento.
 *
 * ── O layout é o das outras telas, e isso não é detalhe ──
 *
 * As classes (`pixTransferComponent`, `mainContent100`, `diplay-inline`, `input`, `buttonSend`)
 * só existem DENTRO de `.pixTransferComponent`, em PixTransfer.scss. A primeira versão desta
 * tela inventou as suas e ficou sem formatação nenhuma: num fluxo de pagamento, tela
 * desalinhada é tela em que ninguém confia para digitar uma conta.
 *
 * ── Dois passos, como nas demais ──
 *
 * Preencher e revisar. A revisão existe porque TED não volta: ver os dados uma vez antes de
 * confirmar é a última chance de pegar um dígito trocado.
 */

const TIPOS_CONTA = [
  { value: "CHECKING_ACCOUNT", label: "Conta corrente" },
  { value: "SAVINGS_ACCOUNT", label: "Conta poupança" },
  { value: "PAYMENT_ACCOUNT", label: "Conta de pagamento" },
  { value: "WAGE_ACCOUNT", label: "Conta salário" },
];

const VAZIO = {
  amount: "",
  receiverName: "",
  receiverDocument: "",
  ispb: "",
  branchNumber: "",
  accountNumber: "",
  accountType: "",
  description: "",
};

export default function TedBank() {
  const [translations, setTranslations] = useState({});
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(VAZIO);
  const [codigo2fa, setCodigo2fa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [recibo, setRecibo] = useState(null);

  useEffect(() => {
    getTranslations(localStorage.getItem("language") || "pt").then(setTranslations);
  }, []);

  const set = (campo) => (event) => {
    setForm({ ...form, [campo]: event.target.value });
    setErro("");
  };

  const documento = form.receiverDocument.replace(/\D/g, "");
  const banco = BANKS.find((b) => b.ispb === form.ispb);

  const completo =
    Number(form.amount) > 0 &&
    form.receiverName.trim() &&
    (documento.length === 11 || documento.length === 14) &&
    form.ispb &&
    form.branchNumber.trim() &&
    form.accountNumber.trim() &&
    form.accountType;

  async function enviar() {
    setErro("");
    setEnviando(true);

    try {
      const res = await CashOutService.createTed({
        externalId: `TED_${Date.now()}`,
        amount: Number(form.amount).toFixed(2),
        receiverName: form.receiverName.trim(),
        // O tamanho do número já diz se é CPF ou CNPJ: um seletor a mais para um dado que o
        // próprio valor informa só dá uma chance extra de errar.
        receiverDocumentType: documento.length === 11 ? "CPF" : "CNPJ",
        receiverDocument: documento,
        ispb: form.ispb,
        branchNumber: form.branchNumber.trim(),
        accountNumber: form.accountNumber.trim(),
        accountType: form.accountType,
        description: form.description.trim() || "TED",
        twoFactorCode: codigo2fa,
      });

      const data = res?.data?.data ?? res?.data;
      setRecibo({
        status: data?.status || "PENDING",
        controlNumber: data?.controlNumber || data?.id,
      });
      setStep(2);
      setForm(VAZIO);
      setCodigo2fa("");
    } catch (err) {
      // A frase vem do servidor e nomeia o campo (agência, tipo de conta, identificador do
      // banco). Num pagamento bancário, o campo que falta é a diferença entre o dinheiro
      // chegar e ficar em trânsito.
      setErro(
        err?.response?.data?.message ||
          "Não foi possível enviar o TED. Confira os dados e tente de novo.",
      );
      setStep(1);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section id="userPage" className="userPage transactionPage">
      <SideMenu />

      <div className="contentData">
        <div className="contentScroll">
          <Quotes />

          <p className="breadcrumb">
            <b>
              <span className="textPrimary">
                {translations.home} | {translations.transfers}
              </span>{" "}
              | TED
            </b>
          </p>
          <br />
          <p className="title">
            <span style={{ fontWeight: "normal" }}>{translations.transfers} |</span> TED
          </p>
          <br />

          <div className="contentSubData">
            <section id="pixTransferComponent" className="pixTransferComponent">
              {/* ── Passo 0: preencher ── */}
              {step === 0 ? (
                <div className="mainContent100">
                  <div className="diplay-inline">
                    <p>Banco</p>
                    <select
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.ispb}
                      onChange={set("ispb")}
                    >
                      {/*
                        Seletor, e não campo livre: há bancos com números de compensação
                        parecidos, e digitar o errado manda o dinheiro para outra instituição.
                        Escolhendo o nome, o ISPB vai certo.
                      */}
                      <option value="">Selecione um banco</option>
                      {BANKS.map((b) => (
                        <option key={b.ispb} value={b.ispb}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="diplay-inline">
                    <p>Agência (sem dígito)</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.branchNumber}
                      onChange={set("branchNumber")}
                    />
                  </div>

                  <div className="diplay-inline">
                    <p>Conta (com dígito)</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.accountNumber}
                      onChange={set("accountNumber")}
                    />
                  </div>

                  <div className="diplay-inline">
                    <p>Tipo de conta</p>
                    <select
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.accountType}
                      onChange={set("accountType")}
                    >
                      {/*
                        Sem opção pré-selecionada de propósito: assumir conta corrente mandaria
                        dinheiro para a conta errada de quem tem poupança no mesmo banco e
                        agência, e desfazer isso é manual.
                      */}
                      <option value="">Selecione</option>
                      {TIPOS_CONTA.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="diplay-inline">
                    <p>{translations.documentCPFCNPJ || "CPF/CNPJ"} (somente números)</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.receiverDocument}
                      onChange={set("receiverDocument")}
                    />
                  </div>

                  <div className="diplay-inline">
                    <p>{translations.name || "Nome do recebedor"}</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.receiverName}
                      onChange={set("receiverName")}
                    />
                  </div>

                  <div className="diplay-inline">
                    <p>Valor (BRL)</p>
                    <input
                      type="number"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      min="0"
                      step="0.01"
                      value={form.amount}
                      onChange={set("amount")}
                    />
                  </div>

                  <div className="diplay-inline">
                    <p>{translations.description || "Descrição"}</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.description}
                      onChange={set("description")}
                    />
                  </div>

                  <div
                    className={"buttonSend " + (completo ? "" : "disabled")}
                    onClick={() => completo && setStep(1)}
                  >
                    {translations.nextArrow || "Continuar →"}
                  </div>
                </div>
              ) : null}

              {/* ── Passo 1: revisar e confirmar ── */}
              {step === 1 ? (
                <div className="mainContent100">
                  <p className="pTitle">Confira antes de enviar</p>
                  <p className="pLabel">
                    TED não volta. Este é o último momento para pegar um dígito trocado.
                  </p>

                  <div className="contentSpaceBetween">
                    <p className="labelGray">Banco</p>
                    <p>{banco?.name || form.ispb}</p>
                  </div>
                  <div className="contentSpaceBetween">
                    <p className="labelGray">Agência / conta</p>
                    <p>
                      {form.branchNumber} / {form.accountNumber}
                    </p>
                  </div>
                  <div className="contentSpaceBetween">
                    <p className="labelGray">Tipo de conta</p>
                    <p>{TIPOS_CONTA.find((t) => t.value === form.accountType)?.label}</p>
                  </div>
                  <div className="contentSpaceBetween">
                    <p className="labelGray">Recebedor</p>
                    <p>
                      {form.receiverName} · {documento.length === 11 ? "CPF" : "CNPJ"} {documento}
                    </p>
                  </div>
                  <div className="contentSpaceBetween">
                    <p className="labelGray">Valor</p>
                    <p>
                      <b>R$ {Number(form.amount).toFixed(2)}</b>
                    </p>
                  </div>

                  <div className="diplay-inline" style={{ marginTop: 18 }}>
                    <p>Código do segundo fator</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px", letterSpacing: 2 }}
                      value={codigo2fa}
                      onChange={(e) =>
                        setCodigo2fa(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                    />
                  </div>
                  {/* É saída de saldo: o código é validado na mesma requisição que envia. */}
                  <p className="labelGray" style={{ fontSize: 12 }}>
                    É saída de saldo — o código é validado junto do envio.
                  </p>

                  {erro ? (
                    <p style={{ color: "#b42318", fontWeight: "bold", fontSize: 13 }}>{erro}</p>
                  ) : null}

                  <div
                    className={"buttonSend " + (enviando || codigo2fa.length !== 6 ? "disabled" : "")}
                    onClick={() => !enviando && codigo2fa.length === 6 && enviar()}
                  >
                    {enviando ? <div className="loader" /> : "Enviar TED"}
                  </div>

                  <div className="backButton" onClick={() => !enviando && setStep(0)}>
                    {translations.back || "Voltar"}
                  </div>
                </div>
              ) : null}

              {/* ── Passo 2: recibo ── */}
              {step === 2 && recibo ? (
                <div className="mainContent100">
                  <p className="pTitle">TED enviado</p>
                  <p className="pLabel">
                    O status muda conforme o banco recebedor processa.
                  </p>

                  <div className="contentSpaceBetween">
                    <p className="labelGray">Status</p>
                    <p>{recibo.status}</p>
                  </div>
                  {recibo.controlNumber ? (
                    <div className="contentSpaceBetween">
                      <p className="labelGray">Número de controle</p>
                      {/*
                        O TED devolve `controlNumber` onde o PIX devolve `endToEndId` — é este
                        o número que identifica a transferência junto ao banco.
                      */}
                      <p>{recibo.controlNumber}</p>
                    </div>
                  ) : null}

                  <div className="buttonSend" onClick={() => { setRecibo(null); setStep(0); }}>
                    Nova transferência
                  </div>
                </div>
              ) : null}
            </section>
          </div>

          <Footer />
        </div>
      </div>
    </section>
  );
}
